import { useMemo } from 'react';
import { useGetProductCategoriesQuery } from '../services/api';
import { useTheme } from '../themes/ThemeProvider';
import { ProductCategory } from '../types';

/**
 * Categories ViewModel - Encapsulates category browsing logic
 * Follows MVVM pattern where this acts as the ViewModel layer
 *
 * Automatically filters categories by the store's allowedHandles from branding config,
 * ensuring multi-tenant category scoping (each storefront only sees its own categories).
 */
export const useCategoriesViewModel = () => {
  const {
    data: categoriesData,
    isLoading,
    error,
  } = useGetProductCategoriesQuery({});
  const { branding } = useTheme();

  const categories = categoriesData?.product_categories || [];
  const allowedHandles = branding?.header?.navigation?.categoryMenu?.allowedHandles;

  // Filter to only active, non-internal, top-level categories
  // Then scope to this store's allowedHandles (multi-tenant filtering)
  const topLevelCategories = useMemo(() => {
    let filtered = categories
      .filter(
        (cat) =>
          cat.is_active &&
          !cat.is_internal &&
          !cat.parent_category_id
      )
      .sort((a, b) => a.rank - b.rank);

    if (allowedHandles?.length) {
      filtered = filtered.filter((cat) => allowedHandles.includes(cat.handle));
    }

    return filtered;
  }, [categories, allowedHandles]);

  // Get a category by its handle
  const getCategoryByHandle = (handle: string): ProductCategory | undefined => {
    const findInTree = (cats: ProductCategory[]): ProductCategory | undefined => {
      for (const cat of cats) {
        if (cat.handle === handle) return cat;
        if (cat.category_children?.length) {
          const found = findInTree(cat.category_children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return findInTree(categories);
  };

  return {
    categories,
    topLevelCategories,
    isLoading,
    error,
    getCategoryByHandle,
  };
};
